// src/pages/TagProductsPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, Loader2 } from 'lucide-react';
import apiClient from '../api/apiClient';
import VerticalContentCard from '../components/Category/VerticalContentCard';
import ContentFilters from '../components/common/ContentFilters';

const TagProductsPage = () => {
  const { tagSlug } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tagName, setTagName] = useState('');
  const [filters, setFilters] = useState({
    sortOrder: 'newest',
    isFree: null,
  });

  // تابع کمکی برای تبدیل اسلاگ به عنوان خوانا (در صورت عدم دریافت از API)
  const slugToTitle = (slug) => {
    return slug.split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // دریافت نام تگ از API
  useEffect(() => {
    const fetchTagName = async () => {
      try {
        const response = await apiClient.get('/products/tags/');
        const tags = response.data;
        const foundTag = tags.find(t => t.slug === tagSlug);
        if (foundTag) {
          setTagName(foundTag.name);
        } else {
          setTagName(slugToTitle(tagSlug));
        }
      } catch (err) {
        console.error('Error fetching tag name:', err);
        setTagName(slugToTitle(tagSlug));
      }
    };
    fetchTagName();
  }, [tagSlug]);

  // دریافت محصولات تگ
  useEffect(() => {
    const fetchProducts = async () => {
      if (!tagSlug) return;
      setLoading(true);
      setError(null);
      try {
        const params = {};
        if (filters.sortOrder === 'newest') params.sort_by = 'newest';
        if (filters.isFree !== null) params.is_free = filters.isFree;

        const response = await apiClient.get(`/products/list/tag/${tagSlug}/`, { params });
        const productsData = Array.isArray(response.data) ? response.data : [];
        setProducts(productsData);
      } catch (err) {
        console.error('Error fetching tag products:', err);
        setError('خطا در بارگذاری محصولات این تگ');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [tagSlug, filters]);

  // تصویر پیش‌فرض برای متا (مطابق با لوگو یا بنر سایت)
  const defaultOgImage = 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/og-image.jpg';
  const pageTitle = tagName ? `محصولات برچسب ${tagName} | فرزانه` : 'محصولات برچسب | فرزانه';
  const pageDescription = tagName
    ? `لیست محصولات مرتبط با برچسب «${tagName}» در فرزانه. شامل درس‌گفتارها، کتاب‌ها و دوره‌های آموزشی.`
    : 'مشاهده محصولات بر اساس برچسب در فرزانه';

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-purple-700" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-500 text-center p-4">{error}</div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={defaultOgImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={defaultOgImage} />
      </Helmet>

      <div className="sub-categories-page rtl container mx-auto p-0 pb-20 max-w-[460px] overflow-x-hidden bg-white">
        <div className="p-4 bg-white border-b border-gray-200">
          <nav className="text-[11px] font-light text-gray-500 mb-2 flex items-center space-x-reverse space-x-1">
            <Link to="/" className="hover:text-indigo-600">خانه</Link>
            <ChevronLeft size={14} className="mt-0.5 text-gray-400" />
            <span className="text-gray-800 font-medium">{tagName}</span>
          </nav>
          <h1 className="text-2xl font-extrabold text-gray-800 truncate">
             {tagName}
          </h1>
        </div>

        <ContentFilters filters={filters} setFilters={setFilters} />

        <div className="p-4 md:p-6">
          {products.length === 0 ? (
            <div className="text-center text-gray-500 py-10">
              <p>هیچ محصولی با این تگ یافت نشد.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {products.map(product => (
                <VerticalContentCard key={product.id} data={product} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TagProductsPage;